// Solution detail — knowledge-base + service-catalog view.
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";

const money = (v: any) => (v == null ? null : `$${Number(v).toLocaleString()}`);

function KV({ k, v }: { k: string; v: ReactNode }) {
  if (v === null || v === undefined || v === "" || v === false) return null;
  return (
    <div className="flex justify-between gap-4 border-b py-1.5 last:border-0">
      <span className="text-xs text-muted-foreground">{k}</span>
      <span className="text-right text-sm">{v}</span>
    </div>
  );
}

function Prose({ title, body }: { title: string; body?: string | null }) {
  if (!body) return null;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent><p className="whitespace-pre-wrap text-sm">{body}</p></CardContent>
    </Card>
  );
}

export default function SolutionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const queryKey = ["/solutions", id];
  const { data, isLoading } = useQuery({ queryKey, queryFn: async () => (await api.get(`/solutions/${id}`)).data, enabled: !!id });
  async function patch(b: Record<string, unknown>) { await api.put(`/solutions/${id}`, b); qc.invalidateQueries({ queryKey }); }
  async function del() {
    if (!data || !confirm(`Delete solution "${data.name}"?`)) return;
    await api.delete(`/solutions/${id}`);
    qc.invalidateQueries({ queryKey: ["/solutions"] });
    navigate("/solutions");
  }
  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Not found.</div>;
  const s = data;

  return (
    <>
      <PageHeader
        title={s.name}
        description={[s.solutionNumber, s.category].filter(Boolean).join(" · ") || undefined}
        actions={
          <>
            <Button variant="outline" onClick={() => patch({ published: !s.published })}>{s.published ? "Unpublish" : "Publish"}</Button>
            <Button variant="outline" onClick={del}><Trash2 className="mr-1 h-4 w-4" />Delete</Button>
          </>
        }
      />
      <div className="space-y-4 p-6">
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="md:col-span-1">
            <CardHeader><CardTitle className="text-base">Overview</CardTitle></CardHeader>
            <CardContent>
              <KV k="Number" v={s.solutionNumber} />
              <KV k="Category" v={s.category} />
              <KV k="Status" v={<Badge variant="secondary">{s.status}</Badge>} />
              <KV k="Published" v={s.published ? "Yes" : "No"} />
              <KV k="Typical price" v={money(s.typicalPrice)} />
              <KV k="Typical duration" v={s.typicalDuration} />
              <KV k="Product" v={s.product ? <Link className="hover:underline" to={`/products/${s.product.id}`}>{s.product.name}</Link> : null} />
              <KV k="Owner" v={s.owner?.name} />
            </CardContent>
          </Card>
          <div className="space-y-4 md:col-span-2">
            <Prose title="Question" body={s.question} />
            <Prose title="Answer" body={s.answer} />
            <Prose title="Description" body={s.description} />
            <Prose title="Deliverables" body={s.deliverables} />
            <Prose title="Methodology" body={s.methodology} />
            {(s.keywords?.length > 0 || s.tags?.length > 0) && (
              <Card>
                <CardHeader><CardTitle className="text-base">Keywords &amp; tags</CardTitle></CardHeader>
                <CardContent className="flex flex-wrap gap-1">
                  {(s.keywords ?? []).map((k: string) => <Badge key={`k-${k}`} variant="secondary">{k}</Badge>)}
                  {(s.tags ?? []).map((t: string) => <Badge key={`t-${t}`} variant="outline">{t}</Badge>)}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
